import { KnownProjectDtoProjectType, SettingsSchemaDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { RJSFSchema } from '@rjsf/utils';
import { convertHyphenated } from '../../../../core/util/string-conversion';
import { SettingsMapper } from '../../mappings/mappings';
import { ProjectModuleFormOutput } from './form-output';
import { formBooleanToOneOf } from '../shared/form-schema.utils';
import { FormOneOf } from '../project-settings-form/form-schema';

export class ProjectModuleFormSchema {
  static FromSettings(
    settings: ProjectModuleFormOutput,
    moduleOptions: SettingsSchemaDto[] | null,
    partyOptions: Array<FormOneOf<string>>,
    projectType?: KnownProjectDtoProjectType,
    readOnly: boolean = false
  ): RJSFSchema {
    let validModuleOptions: SettingsSchemaDto[] = [];
    if (moduleOptions) {
      validModuleOptions = moduleOptions.filter((module) => module.kind === 'module' && module.id);
    }

    const selectedModule = validModuleOptions.find((module) => module.id === settings.schemaID);

    const schema: RJSFSchema = {
      properties: {
        // Only include the schemaID field if we are creating a new module
        ...(moduleOptions !== null && {
          schemaID: {
            type: 'string',
            title: 'Module',
            default: settings.schemaID || '',
            ...(validModuleOptions.length > 0 && {
              oneOf: validModuleOptions.map((module) => ({
                type: 'string',
                title: convertHyphenated(module.name, { capitalizeEachWord: true }),
                // We have asserted that module.id is defined in the filter above
                enum: [module.id!]
              }))
            })
          }
        }),
        status: {
          type: 'string',
          title: 'Status',
          oneOf: [
            {
              type: 'string',
              title: 'Active',
              enum: ['active']
            },
            // We are not using the "advertised" status for now
            // {
            //   type: 'string',
            //   title: 'Advertised',
            //   enum: ['advertised']
            // },
            {
              type: 'string',
              title: 'Inactive',
              enum: ['inactive']
            }
          ],
          default: settings.status || 'active'
        },
        ...(selectedModule?.schema &&
          'occupantRules' in selectedModule?.schema && {
            occupantRules: {
              type: 'string',
              title: 'Which occupants',
              oneOf: [
                {
                  type: 'string',
                  title: 'All',
                  enum: ['all']
                },
                {
                  type: 'string',
                  title: 'Select',
                  enum: ['select']
                },
                {
                  type: 'string',
                  title: 'None',
                  enum: ['none']
                }
              ],
              default: settings.occupantRules || ''
            }
          }),
        teamMemberRules: {
          type: 'string',
          title: 'Which team members',
          oneOf: [
            {
              type: 'string',
              title: 'All',
              enum: ['all']
            },
            {
              type: 'string',
              title: 'Select',
              enum: ['select']
            },
            {
              type: 'string',
              title: 'None',
              enum: ['none']
            }
          ],
          default: settings.teamMemberRules || '',
          readOnly: true // Team members are always "none" for now
        },
        ...(selectedModule?.schema &&
          'externalDeliveryContainerRules' in selectedModule?.schema && {
            externalDeliveryContainerRules: {
              type: 'string',
              title: SettingsMapper.ProjectTypeToExternalDeliveryContainerFieldLabel(projectType),
              oneOf: [
                {
                  type: 'string',
                  title: 'All',
                  enum: ['all']
                },
                {
                  type: 'string',
                  title: 'Dedicated',
                  enum: ['dedicated']
                }
              ],
              default: settings.externalDeliveryContainerRules || ''
            }
          }),
        ...(selectedModule?.schema &&
          'internalDeliveryContainerRules' in selectedModule?.schema && {
            internalDeliveryContainerRules: {
              type: 'string',
              title: SettingsMapper.ProjectTypeToInternalDeliveryContainerFieldLabel(projectType),
              oneOf: [
                {
                  type: 'string',
                  title: 'All',
                  enum: ['all']
                },
                {
                  type: 'string',
                  title: 'Dedicated',
                  enum: ['dedicated']
                }
              ],
              default: settings.internalDeliveryContainerRules || ''
            }
          }),
        ...(selectedModule?.schema &&
          'providerId' in selectedModule?.schema && {
            providerId: {
              type: 'string',
              title: 'Provider',
              default: settings.providerId || '',
              oneOf: partyOptions
            }
          }),
        ...(selectedModule?.schema &&
          'carrierCode' in selectedModule?.schema && {
            carrierCode: {
              type: 'string',
              title: 'Carrier Code',
              // Exactly 3 uppercase alphanumeric characters. This value is
              // embedded in the Lockerpoint barcode, so the format must match.
              pattern: '^[A-Z0-9]{3}$',
              default: settings.carrierCode || ''
            }
          }),
        // Click & Collect specific fields
        ...(selectedModule?.schema &&
          'deactivateUsersAfterDays' in selectedModule?.schema && {
            deactivateUsersAfterDays: {
              // Nullable so the field can be cleared back to the schema default of null, which
              // is how the sweep is switched off. `minimum` keeps the value a positive integer:
              // the server does no type or range validation on it.
              type: ['integer', 'null'],
              title: 'Deactivate customers after (days)',
              minimum: 1,
              description:
                'Deactivates customers who have not received an order after this many days. The ' +
                'customer record is not deleted and remains searchable. Leave blank to never ' +
                'deactivate.'
            }
          }),
        ...(selectedModule?.schema &&
          'deliveredToRequirement' in selectedModule?.schema && {
            deliveredToRequirement: {
              // No default: an unset value means at-booking, and must stay unset when the form is
              // saved untouched (see form-output.ts).
              type: ['string', 'null'],
              title: 'Recipient requirement',
              oneOf: [
                { type: 'string', title: 'At booking', enum: ['at-booking'] },
                { type: 'string', title: 'Deferred (expected later)', enum: ['deferred'] },
                { type: 'string', title: 'None (not expected)', enum: ['none'] }
              ],
              description:
                'Whether orders need a customer. At booking (the default) requires one. Deferred lets ' +
                'an order be booked without one and flags it as missing data once delivered, until a ' +
                'customer is attached. None never needs one. Without a customer, an order number is required.'
            }
          }),
        // Dry Cleaning specific fields
        ...(selectedModule?.schema &&
          'isQrDeliveryOnly' in selectedModule?.schema && {
            isQrDeliveryOnly: {
              type: 'string',
              title: 'QR Code Delivery Only',
              default: settings.isQrDeliveryOnly || 'no',
              oneOf: formBooleanToOneOf()
            }
          }),
        ...(selectedModule?.schema &&
          'appName' in selectedModule?.schema && {
            appName: {
              type: 'string',
              title: 'App Name',
              default: settings.appName || ''
            }
          }),
        ...(selectedModule?.schema &&
          'appInstallationUrl' in selectedModule?.schema && {
            appInstallationUrl: {
              type: 'string',
              title: 'App Installation URL',
              default: settings.appInstallationUrl || ''
            }
          })
      },
      required: ['status', 'occupantRules', 'teamMemberRules']
    };

    if (selectedModule?.schema && 'providerId' in selectedModule?.schema) {
      // Add provider to the required fields if it exists
      schema.required!.push('providerId');
    }
    if (selectedModule?.schema && 'externalDeliveryContainerRules' in selectedModule?.schema) {
      // Add external delivery container rules to the required fields if it exists
      schema.required!.push('externalDeliveryContainerRules');
    }
    if (selectedModule?.schema && 'internalDeliveryContainerRules' in selectedModule?.schema) {
      // Add internal delivery container rules to the required fields if it exists
      schema.required!.push('internalDeliveryContainerRules');
    }

    if (readOnly) {
      schema.readOnly = true;
    }

    return schema;
  }
}
